import { useState, useEffect, useCallback } from 'react';
import cadrageService from '../services/cadrageService';
import i18n from '../i18n/config';
// Importer les types API BlockerRead, BlockerCreate, BlockerUpdate
import { BlockerRead, BlockerCreate, BlockerUpdate } from '../types';

// Interface pour les données du formulaire, alignée sur BlockerCreate/Update
export interface BlockerFormData {
  name: string; // Ajouter 'name' qui est requis dans BlockerBase/Create
  description?: string | null;
  criticality?: string | null; // Utiliser string pour plus de flexibilité
  mitigation_strategy?: string | null;
}

// Interface pour la valeur de retour du hook
interface UseCadrageBlockersReturn {
  blockers: BlockerRead[]; // Utiliser BlockerRead
  isLoading: boolean;
  error: string | null;
  addBlocker: (formData: BlockerCreate) => Promise<BlockerRead | null>; // Utiliser BlockerCreate/Read
  updateBlocker: (blockerId: string, formData: BlockerUpdate) => Promise<BlockerRead | null>; // Utiliser BlockerUpdate/Read
  deleteBlocker: (blockerId: string) => Promise<void>;
  refreshBlockers: () => void;
}

/**
 * Hook pour gérer les verrous technologiques associés à un Cadrage.
 * @param cadrageId - L'ID du cadrage concerné.
 */
export function useCadrageBlockers(cadrageId: string | undefined): UseCadrageBlockersReturn {
  const [blockers, setBlockers] = useState<BlockerRead[]>([]); // Utiliser BlockerRead
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // --- Fetch Blockers ---
  const fetchBlockers = useCallback(async () => {
    if (!cadrageId) {
      setBlockers([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    console.log(`useCadrageBlockers: Chargement des verrous pour le cadrage ${cadrageId}`);
    try {
      // Utiliser BlockerRead pour le type de réponse
      const data = await cadrageService.getBlockers(cadrageId); // Ajouter le slash final
      setBlockers(data || []);
    } catch (err: any) {
      console.error("Erreur lors du chargement des verrous:", err);
      setError(err.response?.data?.detail || err.message || i18n.t('blocker.errors.loadFailed'));
      setBlockers([]);
    } finally {
      setIsLoading(false);
    }
  }, [cadrageId]);

  // Fonction pour rafraîchir manuellement
  const refreshBlockers = useCallback(() => {
    fetchBlockers();
  }, [fetchBlockers]);

  // Charger les verrous au montage ou quand cadrageId change
  useEffect(() => {
    fetchBlockers();
  }, [fetchBlockers]);

  // --- CRUD Operations ---

  // ADD Blocker (attend BlockerCreate, retourne BlockerRead)
  const addBlocker = useCallback(async (formData: BlockerCreate): Promise<BlockerRead | null> => {
    if (!cadrageId) {
      throw new Error(i18n.t('blocker.errors.missingCadrage'));
    }
    setIsLoading(true);
    setError(null);
    try {
      // Utiliser BlockerRead pour le type de réponse
      const data = await cadrageService.createBlocker(cadrageId, formData); // Ajouter slash final
      await fetchBlockers(); // Rafraîchir la liste
      return data;
    } catch (err: any) {
      console.error("Erreur lors de l'ajout du verrou:", err);
      const errorMsg = err.response?.data?.detail || err.message || i18n.t('blocker.errors.addFailed');
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [cadrageId, fetchBlockers]);

  // UPDATE Blocker (attend BlockerUpdate, retourne BlockerRead)
  const updateBlocker = useCallback(async (blockerId: string, formData: BlockerUpdate): Promise<BlockerRead | null> => {
    if (!cadrageId || !blockerId) { // Vérifier aussi cadrageId
      throw new Error(i18n.t('blocker.errors.missingIdsForUpdate'));
    }
    setIsLoading(true);
    setError(null);
    try {
      console.log("[useCadrageBlockers] Payload envoyé pour mise à jour:", JSON.stringify(formData, null, 2));
      // Utiliser BlockerRead pour le type de réponse
      const data = await cadrageService.updateBlocker(cadrageId, blockerId, formData);
      await fetchBlockers(); // Rafraîchir la liste
      return data;
    } catch (err: any) {
      console.error("Erreur lors de la mise à jour du verrou:", err);
      console.error("Détails de l'erreur:", err.response?.data);
      // Gérer les erreurs de validation Pydantic (422)
      let errorMsg = i18n.t('blocker.errors.updateFailed');
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          // Erreur de validation Pydantic
          errorMsg = err.response.data.detail.map((e: any) =>
            `${e.loc?.join('.') || i18n.t('blocker.errors.unknownField')}: ${e.msg}`
          ).join(', ');
        } else if (typeof err.response.data.detail === 'string') {
          errorMsg = err.response.data.detail;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [cadrageId, fetchBlockers]); // Ajouter cadrageId comme dépendance

  // DELETE Blocker
  const deleteBlocker = useCallback(async (blockerId: string): Promise<void> => {
    if (!cadrageId || !blockerId) { // Vérifier aussi cadrageId
      throw new Error(i18n.t('blocker.errors.missingIdsForDelete'));
    }
    setIsLoading(true);
    setError(null);
    try {
      // Utiliser l'endpoint spécifique au Cadrage
      await cadrageService.deleteBlocker(cadrageId, blockerId); // Pas de slash final pour DELETE avec ID
      await fetchBlockers(); // Rafraîchir la liste
    } catch (err: any) {
      console.error("Erreur lors de la suppression du verrou:", err);
      const errorMsg = err.response?.data?.detail || err.message || i18n.t('blocker.errors.deleteFailed');
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [cadrageId, fetchBlockers]); // Ajouter cadrageId comme dépendance

  // --- Return Value ---
  return {
    blockers,
    isLoading,
    error,
    addBlocker,
    updateBlocker,
    deleteBlocker,
    refreshBlockers,
  };
}
